import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

const ParticipantContext = createContext(null);

const STORAGE_KEY = "denso_participant";

export function ParticipantProvider({ children }) {
  const [participant, setParticipant] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  const persist = useCallback((p) => {
    setParticipant(p);
    if (p) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/participants", payload);
    persist(data);
    setProgress(null);
    return data;
  }, [persist]);

  const refreshProgress = useCallback(async () => {
    if (!participant?.id) return null;
    setLoadingProgress(true);
    try {
      const { data } = await api.get(`/participants/${participant.id}/progress`);
      setProgress(data);
      return data;
    } finally {
      setLoadingProgress(false);
    }
  }, [participant]);

  const markVideoWatched = useCallback(async (moduleId) => {
    if (!participant?.id) return;
    await api.post(`/participants/${participant.id}/progress`, { module_id: moduleId });
    await refreshProgress();
  }, [participant, refreshProgress]);

  const resetSession = useCallback(() => {
    persist(null);
    setProgress(null);
  }, [persist]);

  useEffect(() => {
    if (participant?.id) {
      refreshProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?.id]);

  return (
    <ParticipantContext.Provider
      value={{
        participant,
        progress,
        loadingProgress,
        register,
        refreshProgress,
        markVideoWatched,
        resetSession,
      }}
    >
      {children}
    </ParticipantContext.Provider>
  );
}

export function useParticipant() {
  const ctx = useContext(ParticipantContext);
  if (!ctx) throw new Error("useParticipant must be used within ParticipantProvider");
  return ctx;
}
